import { useMemo } from "react";
import styled from "styled-components";
import { createPortal } from "react-dom";
import { useUnit } from "components/Isometry/Isometry.utilities";
import { Icon } from "components/Icons";
import { useActiveUnit } from "components/Unit";
import { getProjectData } from "state/jotai.projectDataHooks";
import Unit from "components/Unit/Unit.model";

/* ==============================
   🔹 HOVER CARD (UI MATCH)
============================== */
const POPUP_WIDTH = 300;
const POPUP_HEIGHT = 200; // approx
const OFFSET = 12;

const HoverBox = styled.div`
  position: fixed;
  top: ${({ y }) => y}px;
  left: ${({ x }) => x}px;
  transform: translate(12px, 12px);

  width: 300px;

  background: rgba(255, 255, 255, 0.9);
  // backdrop-filter: blur(6px);

  border: 2px solid var(--Travbyen-Secondary, #f8f9f1);
  border-radius: 16px;

  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);

  pointer-events: auto;
  z-index: 9999;
`;

/* ==============================
   🔹 LAYOUT PARTS
============================== */
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: 6px 16px;

  background: #f8f9f1;
  // backdrop-filter: blur(4px);
  // -webkit-backdrop-filter: blur(4px);

  border-radius: 16px 16px 0 0;

  //   border-bottom: 1px solid rgba(18, 63, 44, 0.08);
`;
const Title = styled.h3`
  margin: 0;
  color: ${({ clr }) => clr?.primary_text};
`;

const Status = styled.span`
  display: inline-flex;
  align-items: center;

  padding: 4px 8px;
  border-radius: 999px;

  color: #fff;

  background: ${({ statusColor }) => {
    // switch (status?.toLowerCase()) {
    //   case "reserveret":
    //     return "#f0ac3f"; // yellow
    //   case "reserved":
    //     return "#eb5757"; // red
    //   case "ledig":
    //   default:
    //     return "var(--Travbyen-Secondary, #2f6f4e)"; // green
    // }
    return statusColor
  }};
`;

const Price = styled.div`
  margin-bottom: 8px;
  color: ${({ clr }) => clr?.primary_text};
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  row-gap: 8px;
  color: ${({ clr }) => clr?.primary_text};
`;

const Item = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const Body = styled.div`
  padding: 8px 0 8px 16px;
`;
/* ==============================
   🔹 COMPONENT
============================== */
export const InnerDesktopLabel = ({ hoveredId, hoveredPos, clr }) => {
  const units = getProjectData("units", "Units");
  const activeUnit = useActiveUnit();
  // console.log(units,"skks")
  const unit = useUnit();
  const DEBUG_LOCK_POPUP = false; // ← set false when done
  const DEBUG_POSITION = { x: 200, y: 200 };

  const currentUnit = useMemo(() => {
    if (!units?.length) return null;

    // 1️⃣ Hovered unit (highest priority)
    if (hoveredId) {
      const hoveredUnitId = unit(hoveredId, "UnitID");

      if (hoveredUnitId) {
        return units.find((u) => Number(u.UnitID) === Number(hoveredUnitId));
      }
    }

    // 2️⃣ Fallback to active unit
    const activeUnitId = activeUnit?.getId?.();
    if (activeUnitId) {
      return units.find((u) => Number(u.UnitID) === Number(activeUnitId));
    }

    return null;
  }, [units, hoveredId, activeUnit, unit]);
  const cUnit = new Unit(currentUnit);
  // console.log(cUnit,"slso")
  const isRent = cUnit.isRent();
  const price = cUnit.getPrice();

  const visible = DEBUG_LOCK_POPUP || (currentUnit && hoveredPos);
  // console.log(currentUnit,"dkdk")
    const availablePris = cUnit?.data?.Data?.Pris_Tekst_DK?.Value === "";

const basePos = DEBUG_LOCK_POPUP ? DEBUG_POSITION : hoveredPos;

const smartPos = useMemo(() => {
  if (!basePos) return null;

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let x = basePos.x;
  let y = basePos.y;

  // Right overflow → move left
  if (basePos.x + POPUP_WIDTH + OFFSET > vw) {
    x = basePos.x - POPUP_WIDTH - OFFSET;
  }

  // Bottom overflow → move up
  if (basePos.y + POPUP_HEIGHT + OFFSET > vh) {
    y = basePos.y - POPUP_HEIGHT - OFFSET;
  }

  return { x, y };
}, [basePos]);



  const outdoorsFeatures = useMemo(() => {
    if (!currentUnit?.Data) return null;

    const features = [];

    if (currentUnit.Data.Terrasse?.Value) {
      features.push(currentUnit.Data.Terrasse.Label);
    }

    if (currentUnit?.Data?.FranskAltan?.Value) {
      features.push(currentUnit.Data.FranskAltan.Label);
    }

    // Join with comma, or return null if empty
    return features.length ? features.join(", ") : currentUnit.Data.FranskAltan.Label;
  }, [currentUnit]);
  const rawMoveInDate =
  currentUnit?.Data["DATE_FORMAT(Ledig_pr, '%d-%m-%Y')"]?.Value;

const moveInDate = rawMoveInDate
  ? rawMoveInDate.replace(/-/g, ".")
  : "";
  const statusValue = currentUnit?.Data?.UnitStatusName?.Value || "Ledig";

  const statusColor = cUnit.getValue('UnitColor')

if (!visible) return null;

  return createPortal(
  <HoverBox x={smartPos.x} y={smartPos.y}>
      {/* Header */}
      <Header>
        <Title className="caption-medium" clr={clr}>
          {" "}
          {currentUnit?.Data?.Adresse?.Value}
        </Title>
        <Status className="caption-medium" statusColor={statusColor} status={statusValue}>
          {statusValue}
        </Status>
      </Header>
      <Body>
        {/* Price */}
        {availablePris ? (
          <Price className="h5" clr={clr}>
            {price}
            {isRent ? "/md" : ""}
          </Price>
        ) : null}

        {/* Details */}
        <List clr={clr}>
          <Item className="paragraph2-regular">
            <Icon
              isFaIcon={true}
              icon="house"
              sx={{ width: "24px", height: "24px" }}
            />
            {currentUnit?.Data?.SalesTypeName?.Value}
          </Item>

          <Item className="paragraph2-regular">
            <Icon icon="area" sx={{ width: "16px", height: "16px" }} />
            {currentUnit?.Data?.Areal?.Value} m²
          </Item>

          <Item className="paragraph2-regular">
            <Icon icon="rooms" sx={{ width: "16px", height: "16px" }} />
            {currentUnit?.Data?.VaerelserAntal?.Value} værelser
          </Item>

          {/* <Item className="paragraph2-regular">
            <Icon icon="outdoors" sx={{ width: "16px", height: "16px" }} />
            {outdoorsFeatures}
          </Item> */}

          <Item className="paragraph2-regular">
            <Icon
              isFaIcon={true}
              icon="key"
              sx={{ width: "24px", height: "24px" }}
            />
            Indflytning: {moveInDate}
          </Item>
        </List>
      </Body>
    </HoverBox>,
    document.body,
  );
};
